import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ChevronDown, Download, GripVertical, Plus, Trash2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Summary of Results — Wrap-Up tab.
 *
 * Two tabs (Competitive vs Special). Auto-saves to the server on
 * every change. Date-gated to the competition's start_date.
 */
export default function ResultsPanel({ competitionId }) {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('competitive');

  // Map of section.key → { place_n: skater_id }
  const [competitive, setCompetitive] = useState({});
  // [{ id, order, grade_id, grade_name, division, skater_id, placing }, ...]
  const [specialRows, setSpecialRows] = useState([]);

  // ---- Load setup -----------------------------------------------------
  useEffect(() => {
    if (!competitionId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/results/${competitionId}/setup`,
          { withCredentials: true });
        setSetup(res.data);
        // Rehydrate competitive selections from saved doc
        const saved = res.data.saved?.competitive || {};
        const fresh = {};
        for (const sec of res.data.sections || []) {
          const savedSec = saved[sec.key] || {};
          const placings = savedSec.placings || [];
          const byPlace = {};
          placings.forEach(p => { byPlace[p.place] = p.skater_id; });
          fresh[sec.key] = byPlace;
        }
        setCompetitive(fresh);
        setSpecialRows(res.data.saved?.special_events || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load results setup');
      } finally {
        setLoading(false);
      }
    })();
  }, [competitionId]);

  // ---- Auto-save (debounced) ------------------------------------------
  useEffect(() => {
    if (!setup || !competitionId || !setup.available) return undefined;
    const t = setTimeout(async () => {
      const competitiveArr = (setup.sections || []).map(sec => {
        const sel = competitive[sec.key] || {};
        const placings = [];
        for (let i = 1; i <= sec.max_placings; i++) {
          if (sel[i]) placings.push({ place: i, skater_id: sel[i] });
        }
        return {
          grade_id: sec.grade_id,
          grade_name: sec.grade_name,
          discipline_name: sec.discipline_name,
          gender_split: sec.gender_split,
          panel: sec.panel,
          judging_mode: sec.judging_mode,
          placings,
        };
      });
      try {
        await axios.put(`${API}/results/${competitionId}`,
          { competitive: competitiveArr, special_events: specialRows },
          { withCredentials: true });
      } catch (err) {
        // Stay quiet on auto-save errors — only toast on the manual generate path
        console.warn('Auto-save failed:', err.response?.data?.detail || err.message);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [competitive, specialRows, setup, competitionId]);

  // ---- Helpers --------------------------------------------------------
  const placedSidsInSection = (sectionKey) => {
    const sel = competitive[sectionKey] || {};
    return new Set(Object.values(sel).filter(Boolean));
  };

  const selectSkater = (sectionKey, place, skaterId) => {
    setCompetitive(prev => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [place]: skaterId },
    }));
  };

  // Auto-prefill rules (competitive sections only):
  //   • 1-entrant grade → set 1st place
  //   • 2-entrant grade → once 1st is chosen, set 2nd to the other
  //   • 3-entrant grade → once 1st and 2nd are chosen, set 3rd to
  //     the remaining skater
  //   • separate-panels section with 1 entrant in that panel → set 1st
  // Runs whenever the user picks a placing, not just on load.
  useEffect(() => {
    if (!setup) return;
    let changed = false;
    const next = { ...competitive };
    for (const sec of setup.sections || []) {
      const sel = next[sec.key] || {};
      if (sec.entries.length === 1 && !sel[1]) {
        next[sec.key] = { ...sel, 1: sec.entries[0].skater_id };
        changed = true;
        continue;
      }
      if (sec.entries.length === 2 && sel[1] && !sel[2]) {
        const other = sec.entries.find(e => e.skater_id !== sel[1]);
        if (other) {
          next[sec.key] = { ...sel, 2: other.skater_id };
          changed = true;
        }
        continue;
      }
      if (sec.entries.length === 3 && sel[1] && sel[2] && !sel[3]) {
        const other = sec.entries.find(
          e => e.skater_id !== sel[1] && e.skater_id !== sel[2]
        );
        if (other) {
          next[sec.key] = { ...sel, 3: other.skater_id };
          changed = true;
        }
      }
    }
    if (changed) setCompetitive(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup, competitive]);

  // ---- Generate -------------------------------------------------------
  const generate = async () => {
    if (!competitionId) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/results/${competitionId}/generate`, {},
        { withCredentials: true, responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'Summary_of_Results.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Summary of Results downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Special-events: add / delete / reorder -------------------------
  const addSpecialRow = (grade_id, grade_name, discipline_name, division, skater_id, placing) => {
    const nextOrder = specialRows.length
      ? Math.max(...specialRows.map(r => r.order || 0)) + 1
      : 0;
    setSpecialRows([
      ...specialRows,
      {
        id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        order: nextOrder, grade_id, grade_name, discipline_name,
        division, skater_id, placing,
      },
    ]);
  };

  const removeSpecialRow = (id) => {
    setSpecialRows(specialRows.filter(r => r.id !== id));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = specialRows.map(r => r.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    const reordered = arrayMove(specialRows, oldIdx, newIdx)
      .map((r, i) => ({ ...r, order: i }));
    setSpecialRows(reordered);
  };

  // ---- Early returns --------------------------------------------------
  if (!competitionId) {
    return <div className="text-sm text-stone-600">
      Select a competition above to enter results.
    </div>;
  }
  if (loading) {
    return <div className="text-sm text-stone-600">Loading results setup…</div>;
  }
  if (!setup) return null;
  if (!setup.available) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"
           data-testid="results-not-available-banner">
        <h3 className="font-semibold text-amber-900 mb-1">
          Results entry isn&apos;t available yet
        </h3>
        <p className="text-sm text-amber-900">
          The Summary of Results becomes available from <strong>
          {setup.competition.start_date}</strong> (the competition&apos;s start date).
          Pop back in once Day 1 has begun.
        </p>
      </div>
    );
  }

  // ---- Render ---------------------------------------------------------
  return (
    // Outer DocumentsPage right-column container already provides
    // overflow-y-auto — adding another here produces a nested scroll-
    // bar (user-reported, Feb 2026). Keep this wrapper layout-only.
    <div className="space-y-5"
         data-testid="results-panel">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-1 flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#5C4033]" />
          Summary of Results
        </h3>
        <p className="text-sm text-muted-foreground">
          Enter placings for each grade. Changes save automatically. When you&apos;re
          ready, hit Generate to download the Excel workbook (Competitive + Special
          Events sheets).
        </p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 border-b" data-testid="results-tabs">
        {[
          ['competitive', `Competitive Grades (${setup.sections?.length || 0})`],
          ['special', `Special Events (${setup.special_grades?.length || 0} grades)`],
        ].map(([key, lbl]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            data-testid={`results-tab-${key}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === key
                ? 'border-[#5C4033] text-[#5C4033]'
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {activeTab === 'competitive' && (
        <CompetitiveTab
          sections={setup.sections || []}
          competitive={competitive}
          selectSkater={selectSkater}
          placedSidsInSection={placedSidsInSection}
        />
      )}

      {activeTab === 'special' && (
        <SpecialEventsTab
          specialGrades={setup.special_grades || []}
          rows={specialRows}
          addRow={addSpecialRow}
          removeRow={removeSpecialRow}
          sensors={sensors}
          handleDragEnd={handleDragEnd}
        />
      )}

      <Button
        onClick={generate}
        disabled={generating}
        className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-full"
        data-testid="results-generate-btn"
      >
        {generating
          ? <>Generating…</>
          : <><Download className="h-4 w-4 mr-2" /> Generate Summary of Results (.xlsx)</>}
      </Button>
    </div>
  );
}


// =====================================================================
// Competitive Tab
// =====================================================================
function CompetitiveTab({ sections, competitive, selectSkater, placedSidsInSection }) {
  // Group sections by grade_name for visual grouping; render each
  // section's "panel/event" label only when it's distinct (e.g.
  // separate panels for Women's + Men's of the same grade).
  const grouped = useMemo(() => {
    const byGrade = {};
    for (const s of sections) {
      (byGrade[s.grade_name] ||= []).push(s);
    }
    return Object.entries(byGrade);
  }, [sections]);

  if (sections.length === 0) {
    return <div className="text-sm italic text-stone-500">
      No competitive grades found (no events on national / international
      schedules yet).
    </div>;
  }

  return (
    <div className="space-y-3" data-testid="results-competitive-list">
      {grouped.map(([gradeName, secs]) => (
        <CompetitiveGradeBlock
          key={gradeName}
          gradeName={gradeName}
          sections={secs}
          competitive={competitive}
          selectSkater={selectSkater}
          placedSidsInSection={placedSidsInSection}
        />
      ))}
    </div>
  );
}

function CompetitiveGradeBlock({ gradeName, sections, competitive, selectSkater, placedSidsInSection }) {
  const [open, setOpen] = useState(true);
  const totalPlaced = sections.reduce(
    (a, s) => a + Object.values(competitive[s.key] || {}).filter(Boolean).length, 0
  );
  const totalPlaces = sections.reduce((a, s) => a + s.max_placings, 0);
  return (
    <div className="rounded-lg border bg-white" data-testid={`results-grade-${gradeName}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-stone-50"
      >
        <div className="text-left">
          <div className="font-semibold text-[#5C4033]">{gradeName}</div>
          <div className="text-xs text-stone-500">
            {sections.length === 1
              ? `${totalPlaced}/${totalPlaces} placings recorded`
              : `${sections.length} panels · ${totalPlaced}/${totalPlaces} placings recorded`}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t px-4 py-3 space-y-4">
          {sections.map(section => (
            <CompetitiveSectionForm
              key={section.key}
              section={section}
              selections={competitive[section.key] || {}}
              selectSkater={selectSkater}
              placedSids={placedSidsInSection(section.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CompetitiveSectionForm({ section, selections, selectSkater, placedSids }) {
  const panelLabel = useMemo(() => {
    if (section.judging_mode === 'split_event') {
      if (section.gender_split === 'F') return "Women's Event";
      if (section.gender_split === 'M') return "Men's Event";
    }
    if (section.judging_mode === 'separate_panels') {
      if (section.panel === 'F') return "Women's Placings";
      if (section.panel === 'M') return "Men's Placings";
    }
    return null; // combined — no sub-label
  }, [section]);

  return (
    <div data-testid={`results-section-${section.key}`}>
      {panelLabel && (
        <div className="text-sm italic text-[#3D5A3D] mb-2 font-medium">{panelLabel}</div>
      )}
      {/* Grid layout — places fill column-first so a 3-place section
          renders as 1st (top-left), 2nd (bottom-left), 3rd (top-right). */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-2 md:grid-flow-col gap-2">
        {Array.from({ length: section.max_placings }, (_, i) => i + 1).map(place => {
          const selected = selections[place] || '';
          // Pool of selectable entries = ALL entries minus the ones
          // already placed in OTHER slots within this section.
          const pool = section.entries.filter(e =>
            e.skater_id === selected || !placedSids.has(e.skater_id)
          );
          return (
            <div key={place} className="flex items-center gap-2">
              <Label className="w-12 text-sm font-bold text-[#5C4033]">
                {ordinal(place)}
              </Label>
              <Select
                value={selected || undefined}
                onValueChange={(v) => selectSkater(section.key, place, v)}
              >
                <SelectTrigger
                  className="flex-1"
                  data-testid={`results-place-${section.key}-${place}`}
                >
                  <SelectValue placeholder="Select skater…" />
                </SelectTrigger>
                <SelectContent>
                  {pool.map(e => (
                    <SelectItem key={e.skater_id} value={e.skater_id}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// =====================================================================
// Special Events Tab
// =====================================================================
function SpecialEventsTab({ specialGrades, rows, addRow, removeRow, sensors, handleDragEnd }) {
  const [gradeId, setGradeId] = useState('');
  const [division, setDivision] = useState('');
  const [skaterId, setSkaterId] = useState('');
  const [placing, setPlacing] = useState('');

  const selectedGrade = specialGrades.find(g => g.grade_id === gradeId);

  const submit = () => {
    if (!gradeId) { toast.warning('Pick a grade first'); return; }
    if (!skaterId) { toast.warning('Pick a skater'); return; }
    if (!placing) { toast.warning('Pick a placing'); return; }
    addRow(
      gradeId,
      selectedGrade?.grade_name || '',
      selectedGrade?.discipline_name || null,
      division.trim() || null,
      skaterId,
      parseInt(placing, 10),
    );
    setDivision('');
    setSkaterId('');
    setPlacing('');
  };

  return (
    <div className="space-y-4" data-testid="results-special-tab">
      {specialGrades.length === 0 && (
        <div className="text-sm italic text-stone-500">
          No special-event grades found (no events on special-event schedules
          with active entries).
        </div>
      )}

      {specialGrades.length > 0 && (
        <div className="rounded-lg border p-4 space-y-3 bg-stone-50">
          <h4 className="font-semibold text-stone-800">Add a result</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs text-stone-600">Grade</Label>
              <Select value={gradeId} onValueChange={(v) => { setGradeId(v); setSkaterId(''); setPlacing(''); }}>
                <SelectTrigger data-testid="results-special-grade-select">
                  <SelectValue placeholder="Pick…" />
                </SelectTrigger>
                <SelectContent>
                  {specialGrades.map(g => (
                    <SelectItem key={g.grade_id} value={g.grade_id}>
                      {g.grade_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Division (free-text)</Label>
              <Input
                value={division}
                onChange={(e) => setDivision(e.target.value)}
                placeholder="e.g. Under 10 / Boys"
                data-testid="results-special-division-input"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Skater</Label>
              <Select value={skaterId} onValueChange={setSkaterId} disabled={!selectedGrade}>
                <SelectTrigger data-testid="results-special-skater-select">
                  <SelectValue placeholder="Pick…" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedGrade?.entries || []).map(e => (
                    <SelectItem key={e.skater_id} value={e.skater_id}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-600">Placing</Label>
              <Select value={placing} onValueChange={setPlacing} disabled={!selectedGrade}>
                <SelectTrigger data-testid="results-special-placing-select">
                  <SelectValue placeholder="Pick…" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: selectedGrade?.max_placings || 0 }, (_, i) => i + 1).map(p => (
                    <SelectItem key={p} value={String(p)}>{ordinal(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={submit}
            className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
            data-testid="results-special-add-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add to list
          </Button>
        </div>
      )}

      {rows.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
            <table className="w-full text-sm border-collapse"
                   data-testid="results-special-rows-table">
              <thead>
                <tr className="bg-stone-100">
                  <th className="border px-2 py-2 w-8"></th>
                  <th className="border px-3 py-2 text-left">Grade</th>
                  <th className="border px-3 py-2 text-left">Division</th>
                  <th className="border px-3 py-2 text-left">Place</th>
                  <th className="border px-3 py-2 text-left">Skater</th>
                  <th className="border px-3 py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <SortableSpecialRow
                    key={r.id}
                    row={r}
                    skaterLabel={lookupSkaterLabel(r.skater_id, specialGrades)}
                    onRemove={() => removeRow(r.id)}
                  />
                ))}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SortableSpecialRow({ row, skaterLabel, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <tr ref={setNodeRef} style={style}
        data-testid={`results-special-row-${row.id}`}>
      <td className="border px-2 py-1 text-center text-stone-400 cursor-grab"
          {...attributes} {...listeners}
          data-testid={`results-special-drag-${row.id}`}>
        <GripVertical className="h-4 w-4 inline" />
      </td>
      <td className="border px-3 py-2">{row.grade_name}</td>
      <td className="border px-3 py-2">{row.division || '—'}</td>
      <td className="border px-3 py-2 font-semibold">{ordinal(row.placing)}</td>
      <td className="border px-3 py-2">{skaterLabel}</td>
      <td className="border px-3 py-2 text-center">
        <button type="button" onClick={onRemove}
                className="text-red-600 hover:text-red-800"
                data-testid={`results-special-remove-${row.id}`}
                aria-label="Remove this row">
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}


// =====================================================================
// Tiny helpers
// =====================================================================
function ordinal(n) {
  if (!n) return '';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function lookupSkaterLabel(skaterId, specialGrades) {
  if (!skaterId) return '';
  for (const g of specialGrades) {
    for (const e of (g.entries || [])) {
      if (e.skater_id === skaterId) return e.label;
    }
  }
  return '(skater removed)';
}
